/**
 * Resolves a section background color from CMS-authored `classNames`, matching the non-PL flow.
 *
 * Non-PL sections (e.g. `rowOfImageTextBlocks`, the Contentful source of `FEATURE_CARDS`) apply the
 * raw `background-color--*` class to the DOM and let the global stylesheet color it — the selector
 * `.background-color--<key>` is generated from the SCSS `$colors` map
 * (`src/quince-styles/scss/utils/variable.scss`, emitted by `src/quince-styles/scss/common.scss`).
 * PL widgets (especially the DS branch) do not forward the raw class, so we resolve the same
 * `background-color--<key>` vocabulary to the same color here and apply it as an inline
 * `background-color`. This list mirrors `$colors` 1:1 so PL and non-PL render identical bands for any
 * authored color; keep the two in sync when `$colors` changes.
 *
 * PL widgets receive these class strings on their `*WidgetData` once the proto carries them; until
 * then callers cast `classNames`. When no known class is present this returns `undefined` and the
 * widget's own default background applies.
 */

/* eslint-disable i18next/no-literal-string -- CMS class-name keys + color values, not user-facing copy */
// Mirrors `$colors` in src/quince-styles/scss/utils/variable.scss (key -> hex).
const CLASS_NAME_TO_BACKGROUND: readonly (readonly [className: string, color: string])[] = [
  ['background-color--cantalope', '#ffa273'],
  ['background-color--offWhite', '#eeeeec'],
  ['background-color--black', '#21201f'],
  ['background-color--neutralGray', '#606060'],
  ['background-color--petal', '#e5ccbc'],
  ['background-color--white', '#ffffff'],
  ['background-color--paleBlue', '#c8d3f1'],
  ['background-color--navy', '#2d313f'],
  ['background-color--umber', '#85351b'],
  ['background-color--warmGray', '#d0d3bb'],
  ['background-color--gray', '#363940'],
  ['background-color--green', '#2d822b'],
  ['background-color--midGray', '#888888'],
  ['background-color--lightGray', '#bdbdbc'],
  ['background-color--pantone', '#e3e9f8'],
  ['background-color--darkGray2', '#757575'],
  ['background-color--newSiteRed', '#af3535'],
  ['background-color--red', '#d24343'],
  ['background-color--messageBackground', '#f7f7f7'],
  ['background-color--gray84', '#d7d7d7'],
  ['background-color--gray85', '#d9d9d9'],
  ['background-color--stone', '#dfdace'],
  ['background-color--lightestGray', '#f5f5f5'],
  ['background-color--offWhiteHover', '#f7f7f5'],
]
/* eslint-enable i18next/no-literal-string */

export function resolveSectionBackground(classNames?: readonly string[]): string | undefined {
  if (!Array.isArray(classNames)) return undefined
  for (const className of classNames) {
    const match = CLASS_NAME_TO_BACKGROUND.find(([cls]) => cls === className)
    if (match) return match[1]
  }
  return undefined
}
